import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Upload, AlertTriangle, CheckCircle2, FileText, X, ShieldCheck,
} from 'lucide-react';
import { adminPost, adminDelete, errMsg } from './api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImportTab({ setup, onAfter }) {
  const [collection, setCollection] = useState('bookings');
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [preview, setPreview] = useState(null);   // dry-run report
  const [confirmText, setConfirmText] = useState('');
  const [committing, setCommitting] = useState(false);

  // Reset preview when collection or file changes — the staged upload_id
  // is keyed to the previous file and would no longer match.
  const resetPreview = () => { setPreview(null); setConfirmText(''); };

  const handleFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    resetPreview();
  };

  const handleValidate = async () => {
    if (!file) { toast.error('Pick a JSON or CSV file first.'); return; }
    setValidating(true);
    resetPreview();
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await adminPost(`${API}/api/admin/db/import/${collection}/validate`, fd);
      setPreview(res.data);
      toast.success('File validated — review the preview below.');
    } catch (e) {
      toast.error(await errMsg(e, 'Validation failed'));
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async () => {
    if (!preview?.upload_id) return;
    if (confirmText.trim() !== 'IMPORT') {
      toast.error('Type IMPORT in capitals to confirm.');
      return;
    }
    setCommitting(true);
    try {
      const res = await adminPost(
        `${API}/api/admin/db/import/${collection}/commit`,
        { upload_id: preview.upload_id, mode: 'merge' }
      );
      const r = res.data;
      toast.success(
        `Import complete: ${r.inserted} inserted, ${r.updated} updated`
        + (r.failed ? `, ${r.failed} failed` : '')
        + (r.guests_skipped ? `, ${r.guests_skipped} guests skipped` : '')
      );
      setFile(null);
      resetPreview();
      onAfter?.();
    } catch (e) {
      toast.error(await errMsg(e, 'Commit failed'));
    } finally {
      setCommitting(false);
    }
  };

  const handleDiscard = async () => {
    if (preview?.upload_id) {
      try { await adminDelete(`${API}/api/admin/db/import/staging/${preview.upload_id}`); }
      catch (e) { console.debug('discard staging failed (non-fatal, TTL will clean):', e?.message || e); }
    }
    setFile(null);
    resetPreview();
  };

  const importable = setup.collections.map(c => c.collection);
  const validRows = preview ? (preview.will_insert + preview.will_update) : 0;

  return (
    <Card className="p-6" data-testid="import-card">
      <h2 className="text-lg font-bold uppercase mb-4 flex items-center gap-2">
        <Upload className="w-5 h-5 text-indigo-600" /> Import Collection
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">Target Collection</Label>
          <Select
            value={collection}
            onValueChange={(v) => { setCollection(v); resetPreview(); }}
            disabled={!!preview}
          >
            <SelectTrigger data-testid="import-collection-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {importable.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase font-bold text-muted-foreground mb-1 block">File (JSON or CSV)</Label>
          <Input
            type="file"
            accept=".json,.csv,application/json,text/csv"
            onChange={handleFile}
            disabled={!!preview}
            data-testid="import-file"
          />
          {file && (
            <p className="text-xs text-muted-foreground mt-1">
              Selected: <span className="font-mono">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>
      </div>

      <div className="mb-4 p-3 rounded-xl bg-indigo-50 border border-indigo-200">
        <p className="text-xs text-indigo-900">
          <strong>Mode: Merge</strong> — rows are matched by <code>id</code>. Existing rows
          are <strong>updated</strong>; new rows are <strong>inserted</strong>. Nothing is deleted.
          Sensitive fields (password hashes, payment tokens, OTPs) are stripped on the server before
          any DB write — they cannot be smuggled in. Replace mode + Excel will arrive in Phase 3.
        </p>
      </div>

      {!preview && (
        <Button
          onClick={handleValidate}
          disabled={!file || validating}
          className="btn-primary"
          data-testid="validate-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          {validating ? 'Validating...' : 'Validate File'}
        </Button>
      )}

      {preview && (
        <ImportPreview
          preview={preview}
          collection={collection}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
          committing={committing}
          validRows={validRows}
          onCommit={handleCommit}
          onDiscard={handleDiscard}
        />
      )}
    </Card>
  );
}

function ImportPreview({
  preview, collection, confirmText, setConfirmText,
  committing, validRows, onCommit, onDiscard,
}) {
  return (
    <div className="mt-2 border-t pt-5" data-testid="import-preview">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Dry-Run Preview
        </h3>
        <Button variant="ghost" size="sm" onClick={onDiscard} data-testid="discard-btn">
          <X className="w-4 h-4 mr-1" /> Discard
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <PreviewTile label="Will Insert" value={preview.will_insert}
                     bg="bg-emerald-50" border="border-emerald-200" fg="text-emerald-700"
                     dataTestid="will-insert" />
        <PreviewTile label="Will Update" value={preview.will_update}
                     bg="bg-blue-50" border="border-blue-200" fg="text-blue-700"
                     dataTestid="will-update" />
        <PreviewTile label="Skipped" value={preview.skipped_total}
                     bg="bg-amber-50" border="border-amber-200" fg="text-amber-700"
                     dataTestid="skipped" />
        <PreviewTile label="Sensitive Stripped" value={preview.sensitive_fields_stripped}
                     bg="bg-red-50" border="border-red-200" fg="text-red-700"
                     dataTestid="sensitive-stripped" />
      </div>

      {preview.sensitive_field_names?.length > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200">
          <p className="text-xs font-bold text-red-900 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> These sensitive fields were dropped:
          </p>
          <p className="text-xs text-red-800 mt-1 font-mono break-words">
            {preview.sensitive_field_names.join(', ')}
          </p>
        </div>
      )}

      {preview.guests_skipped > 0 && (
        <div
          className="mb-4 p-3 rounded-xl bg-amber-50 border-2 border-amber-300"
          data-testid="guests-skipped-note"
        >
          <p className="text-xs font-bold text-amber-900">
            ⓘ {preview.guests_skipped} guest row{preview.guests_skipped !== 1 ? 's' : ''} will be skipped
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Guest customers cannot be re-imported on their own — they only
            exist as fields on a booking. Re-importing the bookings collection
            (or creating new bookings under the guest's phone) restores them.
          </p>
        </div>
      )}

      {preview.skipped?.length > 0 && (
        <details className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
          <summary className="text-xs font-bold cursor-pointer text-slate-700">
            Skipped rows ({preview.skipped_total} total — first {preview.skipped.length} shown)
          </summary>
          <ul className="text-xs mt-2 space-y-1 text-slate-700">
            {preview.skipped.map((s) => (
              <li key={`${s.row}-${s.id || s.reason}`} className="font-mono">
                Row {s.row}{s.id ? ` (id=${s.id})` : ''}:{' '}
                <span className="text-red-700">{s.reason}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {validRows > 0 ? (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-sm font-bold text-amber-900 flex items-center gap-1 mb-2">
            <AlertTriangle className="w-4 h-4" /> Confirm the import
          </p>
          <p className="text-xs text-amber-800 mb-3">
            This will write {validRows} row{validRows === 1 ? '' : 's'} to the
            <strong> {collection}</strong> collection. To proceed, type <strong>IMPORT</strong> in capitals below.
          </p>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Type IMPORT to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="max-w-xs"
              data-testid="confirm-text"
            />
            <Button
              onClick={onCommit}
              disabled={committing || confirmText.trim() !== 'IMPORT'}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="commit-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {committing ? 'Committing...' : 'Confirm Import'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl bg-slate-100 border border-slate-200 text-sm text-slate-700">
          Nothing valid to import. Fix the file and try again.
        </div>
      )}
    </div>
  );
}

function PreviewTile({ label, value, bg, border, fg, dataTestid }) {
  return (
    <div className={`p-3 rounded-xl ${bg} border ${border}`}>
      <p className={`text-[10px] uppercase font-bold ${fg}`}>{label}</p>
      <p className="text-2xl font-black tabular-nums" data-testid={dataTestid}>{value}</p>
    </div>
  );
}
